/**
 * Junto Frontend Modal Component
 * 
 * This is the base modal component that all Junto frontend systems use.
 * It maintains exact visual equivalence to the current Sol Room modal styles
 * while being configurable for other studios.
 */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { solRoomTheme } from '../../themes/sol-room.js';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme?.shadows?.overlay?.dark || solRoomTheme.shadows.overlay.dark};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  overflow: hidden;
  touch-action: none;
  /* Performance optimization: prepare for opacity animation */
  will-change: opacity;
  
  @media (max-width: ${props => props.theme?.breakpoints?.tablet || solRoomTheme.breakpoints.tablet}) {
    padding: 10px;
    align-items: flex-start;
    padding-top: 20px;
  }
`;

const ModalContainer = styled(motion.div)`
  background: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
  border-radius: 12px;
  box-shadow: 0 20px 60px ${props => props.theme?.shadows?.dark || solRoomTheme.shadows.dark};
  max-width: ${props => props.$maxWidth || '400px'};
  width: 100%;
  ${props => props.$fixedHeight ? `
    height: 95vh;
    max-height: 95vh;
  ` : `
    max-height: 95vh;
  `}
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  /* Performance optimizations */
  will-change: transform, opacity;
  transform: translateZ(0); /* Force GPU layer */
  backface-visibility: hidden; /* Prevent flickering */
  touch-action: auto;
  
  @media (max-width: ${props => props.theme?.breakpoints?.tablet || solRoomTheme.breakpoints.tablet}) {
    width: calc(100vw - 20px);
    max-width: calc(100vw - 20px);
    ${props => props.$fixedHeight ? `
      height: calc(100vh - 40px);
      max-height: calc(100vh - 40px);
    ` : `
      max-height: calc(100vh - 40px);
    `}
    overflow: hidden;
    box-sizing: border-box;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  background: transparent;
  color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  z-index: 1;
  
  &:hover {
    background: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
    color: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ModalContent = styled.div`
  padding: 32px;
  flex: 1;
  overflow: ${props => props.$scrollable ? 'auto' : 'hidden'};
  display: flex;
  flex-direction: column;
  ${props => props.$scrollable ? `
    /* Enable keyboard navigation for scrollable content */
    tabindex: 0;
    outline: none;
  ` : ''}
  
  @media (max-width: ${props => props.theme?.breakpoints?.tablet || solRoomTheme.breakpoints.tablet}) {
    padding: 24px;
  }
`;

const Modal = ({ 
  isOpen, 
  onClose, 
  children, 
  maxWidth = '400px',
  showCloseButton = true,
  fixedHeight = false,
  scrollable = false,
  theme,
  ...props 
}) => {
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.95,  // Reduced from 0.8 for smoother animation
      y: 20         // Reduced from 50 for less dramatic entrance
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0 
    }
  };

  // Handle escape key and scroll lock
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    const preventScroll = (e) => {
      // Allow scrolling within modal content when scrollable
      if (scrollable) {
        const modalContent = e.target.closest('[data-modal-content]');
        if (modalContent) {
          return; // Allow scrolling in modal content
        }
      }
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const preventTouchMove = (e) => {
      // Allow touch scrolling within modal content when scrollable
      if (scrollable) {
        const modalContent = e.target.closest('[data-modal-content]');
        if (modalContent) {
          return; // Allow touch scrolling in modal content
        }
      }
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      
      // Simplified scroll lock - store position and apply minimal styles
      const scrollY = window.scrollY;
      document.body.setAttribute('data-scroll-y', scrollY.toString());
      
      // Get scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Apply minimal scroll lock styles - batch all updates
      requestAnimationFrame(() => {
        document.body.style.cssText = `
          position: fixed;
          top: -${scrollY}px;
          left: 0;
          right: 0;
          overflow: hidden;
          padding-right: ${scrollbarWidth}px;
        `;
        document.documentElement.style.overflow = 'hidden';
      });
      
      // Only prevent scrolling on overlay, not modal content
      if (!scrollable) {
        document.addEventListener('wheel', preventScroll, { passive: false });
        document.addEventListener('touchmove', preventTouchMove, { passive: false });
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      
      if (!scrollable) {
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventTouchMove);
      }
      
      // Restore scroll position and clear styles
      const scrollY = document.body.getAttribute('data-scroll-y');
      
      // Clear all scroll lock styles in one operation
      requestAnimationFrame(() => {
        document.body.style.cssText = '';
        document.documentElement.style.overflow = '';
        
        // Restore scroll position
        if (scrollY) {
          const scrollPosition = parseInt(scrollY);
          window.scrollTo(0, scrollPosition);
          document.body.removeAttribute('data-scroll-y');
        }
      });
    };
  }, [isOpen, onClose, scrollable]);

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={onClose}
          theme={theme}
        >
          <ModalContainer
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            $maxWidth={maxWidth}
            $fixedHeight={fixedHeight}
            theme={theme}
            data-modal-container
            {...props}
          >
            {showCloseButton && (
              <CloseButton onClick={onClose} theme={theme}>
                ×
              </CloseButton>
            )}
            <ModalContent 
              theme={theme} 
              $scrollable={scrollable}
              tabIndex={scrollable ? 0 : undefined}
              style={scrollable ? { outline: 'none' } : undefined}
              data-modal-content={scrollable ? 'true' : undefined}
            >
              {children}
            </ModalContent>
          </ModalContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default Modal;